import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DataRole } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DomainPlan, DomainResult, SelectPlan } from '@mode-switch/query';
import {
  EMPTY_ENCODING,
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncFilterField,
  Encoding,
  FilterType,
} from '@mode-vegas/vegas';
import { TempActions } from '@mode/charts/domain';
import { Flamingo, makeFilters } from '@mode/charts/util-vegas';
import { ReportFilterControlHelpers } from '@mode/filters/util';
import { QueryActionType, ofType } from '@mode/legacy/util-domain';
import { fromReportFilterSelectors, reportFilterActions } from '@mode/report-viewer/domain';
import { FlamingoTypes, ReportTypes } from '@mode/shared/contract-common';
import { ReportFilterOptionsFactory, isPresent, makeFilteredRefId, toDictionary } from '@mode/shared/util-js';
import { DomainQueryBuilder } from '@mode/shared/util-switch';
import { recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade, FlamingoActions, SwitchAttrsActions, SwitchViewActions } from '@mode/switch-views/contract';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';
import { Observable, combineLatest, debounceTime, filter, map, startWith, switchMap, withLatestFrom } from 'rxjs';
import { Actions } from '~/actions';
import { watchMap } from '../rxjs-operators/watchMap';
import { AppStore } from '../types/main.types';

export interface FilterPlan {
  refId: string;
  queryToken: string;
  queryRunToken: string;
  plan: DomainPlan<DataRole>;
}

export class FlamingoFilterService {
  static $inject = ['RxStoreService', 'actions$', 'DataViewFacade'];

  filterFieldLoadingStates$: Observable<{ [alias: string]: FlamingoTypes.PlanStatus }>;

  constructor(rxStore: AppStore, actions$: Observable<Actions>, dataViewFacade: DataViewFacade) {
    this.filterFieldLoadingStates$ = dataViewFacade.datasetResults$.pipe(
      switchMap((datasetResults) =>
        combineLatest(
          datasetResults.map((ds) =>
            rxStore
              .select(fromFlamingoSelectors.createPlanIdSelector(makeFilteredRefId(ds.queryToken)))
              .pipe(
                switchMap((planId) =>
                  combineLatest([
                    rxStore.select(fromFlamingoSelectors.createStateSelector(planId)),
                    rxStore.select(fromFlamingoSelectors.createPlanSelector(planId)),
                  ])
                )
              )
          )
        )
      ),
      map((result) => result.filter((arg) => arg.every(isPresent))),
      filter((result) => result.length > 0),
      map((result) =>
        result.reduce(
          (filterFieldStates, [state, plan]) => {
            if (SelectPlan.isDomain(plan)) {
              for (const { field } of plan.parts) {
                filterFieldStates[field.alias] ??= state;

                switch (filterFieldStates[field.alias]) {
                  case FlamingoTypes.PlanStatus.Succeeded:
                  case FlamingoTypes.PlanStatus.Failed: {
                    filterFieldStates[field.alias] = state;
                    break;
                  }
                }
              }
            }

            return filterFieldStates;
          },
          {} as {
            [alias: string]: FlamingoTypes.PlanStatus;
          }
        )
      )
    );

    // Generate filter domain queries
    actions$
      .pipe(
        ofType(
          QueryActionType.BatchUpdateQueries,
          TempActions.Type.DestroyQuerySuccess,
          SwitchViewActions.pollingSwitchViewCompleted,
          SwitchViewActions.loadGfDataViews,
          SwitchViewActions.loadEditorViewsSuccess,
          SwitchViewActions.refreshEditorViewsSuccess,
          SwitchViewActions.loadImportedDatasetSuccess
        ),
        withLatestFrom(dataViewFacade.datasetResults$, (_, datasetResults) => datasetResults),
        map((datasetResults) => datasetResults.map((d) => d.queryToken)),
        watchMap((queryToken) =>
          dataViewFacade.createRunReadyByQueryToken(queryToken).pipe(
            filter((ready) => ready),
            switchMap(() => dataViewFacade.createQueryRunTokenByQueryToken(queryToken)),
            filter(isPresent),
            switchMap((runToken) =>
              combineLatest([
                actions$.pipe(
                  ofType(
                    // get initial domain values for filters
                    reportFilterActions.loadReportFiltersSuccess,
                    reportFilterActions.showRelevantValuesSuccess,
                    // respond to create filter action so it gets options filled in
                    reportFilterActions.createReportFilterSuccess,
                    // type changed, may need to refetch the domain. Eg. list vs range
                    reportFilterActions.updateReportFilterTypeSuccess
                  ),
                  startWith(null)
                ),
                actions$.pipe(
                  ofType(
                    reportFilterActions.filterSelectionChange,
                    reportFilterActions.resetReportFilters,
                    reportFilterActions.resetReportFilter
                  ),
                  withLatestFrom(rxStore.select(fromReportFilterSelectors.selectFilterDefinitionsArray)),
                  filter(([, defs]) => defs.some((def) => def.showRelevantValuesOnly)),
                  startWith(null)
                ),
                actions$.pipe(
                  ofType(
                    SwitchAttrsActions.updateSwitchViewAttrSuccess,
                    SwitchAttrsActions.deleteSwitchViewAttrSuccess
                  ),
                  filter((action) => action.queryToken === queryToken),
                  startWith(null)
                ),
              ]).pipe(
                map(() => {
                  return runToken;
                })
              )
            ),
            withLatestFrom(
              dataViewFacade.model$,
              rxStore.select(fromReportFilterSelectors.selectFilterControlsWithCurrentSelections)
            ),
            map(([runToken, model, filters]) => this.buildFilterDomainPlan(queryToken, runToken, model, filters)),
            filter(isPresent)
          )
        )
      )
      .subscribe((result) => {
        recordEvent('flamingo-query', 'generate-filter-plan', result);

        rxStore.dispatch(
          FlamingoActions.executePlan({
            allowRefresh: true,
            ...result,
          })
        );
      });

    // Generate filter options
    dataViewFacade.datasetResults$
      .pipe(
        switchMap((datasetResults) =>
          combineLatest(
            datasetResults.map((ds) =>
              rxStore
                .select(fromFlamingoSelectors.createPlanIdSelector(makeFilteredRefId(ds.queryToken)))
                .pipe(switchMap((planId) => rxStore.select(fromFlamingoSelectors.createDomainRecordsSelector(planId))))
            )
          )
        ),
        map((domains) =>
          domains.reduce((merged: FlamingoTypes.DomainHash, next) => {
            if (merged == null) return next;
            if (next == null) return merged;

            return Object.keys(next).reduce((memo: FlamingoTypes.DomainHash, alias) => {
              const cDomain = memo[alias];
              const nDomain = next[alias];

              // No next, return current
              if (nDomain == null) {
                return { ...memo, [alias]: cDomain };
              }

              // No current, return next
              if (cDomain == null) {
                return { ...memo, [alias]: nDomain };
              }

              // Mismatched types, return next?
              // I think we'd wanna know about this happening..
              if (cDomain.type !== nDomain.type) {
                return { ...memo, [alias]: nDomain };
              }

              if (cDomain.type === Attr.Type.Continuous && nDomain.type === Attr.Type.Continuous) {
                const domain = DomainResult.Continuous.merge(cDomain, nDomain);
                return { ...memo, [alias]: domain };
              } else if (cDomain.type === Attr.Type.Discrete && nDomain.type === Attr.Type.Discrete) {
                const domain = DomainResult.Discrete.merge(cDomain, nDomain);
                return { ...memo, [alias]: domain };
              }

              return memo;
            }, merged);
          }, {})
        ),
        debounceTime(16),
        withLatestFrom(
          rxStore.select(fromReportFilterSelectors.selectFilterDefinitions),
          this.filterFieldLoadingStates$
        ),
        map(([domains, filters, statuses]) => {
          return Object.keys(filters)
            .map((token) => filters[token])
            .filter(
              // Allow through once a filter's domain req(s) are no longer in a pending state
              ({ token }) =>
                statuses[token] === FlamingoTypes.PlanStatus.Succeeded ||
                statuses[token] === FlamingoTypes.PlanStatus.Failed
            )
            .filter(({ token }) => domains[token])
            .map((filter) => {
              const domain = domains[filter.token];
              switch (filter.filterType) {
                case ReportTypes.ReportFilterType.Single:
                case ReportTypes.ReportFilterType.Multi:
                  if (domain.type === Attr.Type.Discrete) {
                    return {
                      token: filter.token,
                      options: {
                        filterType: filter.filterType,
                        values: ReportFilterOptionsFactory.buildFlamingoDiscreteValueOptions(
                          filter.formulaType,
                          domain
                        ).toJS(),
                      } as ReportTypes.ReportFilterOptions,
                    };
                  }
                  break;
                case ReportTypes.ReportFilterType.Range:
                  if (domain.type === Attr.Type.Continuous) {
                    return {
                      token: filter.token,
                      options: {
                        filterType: ReportTypes.ReportFilterType.Range,
                        values: {
                          min: domain.min,
                          max: domain.max,
                        },
                      } as ReportTypes.ReportFilterOptions,
                    };
                  }
                  break;
              }
            })
            .filter((f) => f != null);
        })
      )

      .subscribe((optionsWithToken) =>
        rxStore.dispatch(
          reportFilterActions.loadReportFilterOptsSuccess({
            options: toDictionary(optionsWithToken, (option) => {
              switch (option.options.filterType) {
                case ReportTypes.ReportFilterType.Range:
                  return {
                    filterType: option.options.filterType,
                    values: {
                      min: option.options.values.min,
                      max: option.options.values.max,
                    },
                  };
                case ReportTypes.ReportFilterType.Single:
                  return {
                    filterType: option.options.filterType,
                    values: option.options.values,
                  };
                case ReportTypes.ReportFilterType.Multi:
                  return {
                    filterType: option.options.filterType,
                    values: option.options.values,
                  };
              }
            }),
          })
        )
      );
  }

  buildFilterDomainPlan(
    queryToken: string,
    queryRunToken: string,
    model: FlamingoTypes.ModeModel,
    filters: ReportTypes.ReportFilterControl[],
    allowedTokens?: string[]
  ): FilterPlan | void {
    const adapter = new FlamingoAdapter();

    /**
     * If an allow list is provided, determine if a given filter (by token) will
     * be included in the domain fields
     */
    const willIncludeInDomainFields = (token: string): boolean => {
      if (allowedTokens !== undefined) {
        return allowedTokens.includes(token);
      }

      return true;
    };

    /**
     * Returns true if an EncFilter has showRelevantValuesOnly enabled, false otherwise
     */
    const isShowRelevantValues = (
      encFilter: EncFilterField
    ): encFilter is EncDiscreteFilterField | EncDiscreteBooleanFilterField => {
      switch (encFilter.filterType) {
        case FilterType.DiscreteBoolean:
        case FilterType.Discrete: {
          return encFilter.showRelevantValuesOnly;
        }
        default: {
          return false;
        }
      }
    };

    ///
    ///

    /**
     * Filters that are valid per the query token (ie. view)
     */
    const validFilters = Flamingo.getValidReportFilters(model, queryToken, filters);

    /**
     * Fields for the domain query. If {@link allowedTokens} is provided, this will
     * only include filters with a token that has an entry in the allow list
     */
    const domainFields = validFilters
      .filter((control) => willIncludeInDomainFields(control.token))
      .map((control): FlamingoTypes.ModeFields[number] => ({
        discrete: control.controlType !== ReportTypes.ReportFilterControlType.Range,
        formula: control.formula,
        alias: control.token,
      }));

    /**
     * Encoding hosting the filters we _may_ need to add to queries of the domain plan;
     * the filter builder APIs require an encoding
     */
    const encoding: Encoding = {
      ...EMPTY_ENCODING,
      filter: validFilters.map(
        (control): EncFilterField => ({
          ...ReportFilterControlHelpers.toEncFilterField(control),
          id: control.token,
        })
      ),
    };

    /**
     * Domain plan for getting the range/values of our filter fields
     */
    let plan = DomainQueryBuilder.buildDomainQuery(adapter, model, queryToken, domainFields);

    /**
     * Candidate filters we _may_ add to a given query of the domain plan. If a field of the
     * domamin plan has `showRelevantValuesOnly` enabled then we need to add the _other_
     * report filters (if any) to its query; this will limit the result to the relevant
     * values
     */
    const planFilters = makeFilters(model, queryToken, encoding, new Map());

    /**
     * If a field of the plan has a `showRelevantValuesOnly` enabled filter (#1) then
     * we must add the other report filters to the field's query in the plan (#2)
     */
    for (const part of plan.parts) {
      // #1) showRelevantValuesOnly enabled filter
      const showRelevantEncFilter = encoding.filter.find(
        (encFilter) => encFilter.id === part.field.alias && isShowRelevantValues(encFilter)
      );

      if (showRelevantEncFilter) {
        const otherFilters = planFilters.filter(
          // Drop out the field's own filter
          (planFilter) => planFilter.field.alias !== showRelevantEncFilter.id
        );

        plan = {
          ...plan,
          queries: plan.queries.map((query) =>
            query.name === part.query
              ? // #2) Add _other_ filters to this field's query
                { ...query, filters: otherFilters }
              : query
          ),
        };
      }
    }

    if (plan.queries.length) {
      return {
        refId: makeFilteredRefId(queryToken),
        queryToken,
        queryRunToken,
        plan,
      };
    }
  }
}
